import { gesReadJSON, postJSON } from "../api.js";
import { cls, esc } from "./format.js";
import { toast } from "./modals.js";
import { svgRadar } from "../charts/radar.js";

/**
 * Shared UI building blocks for the redesigned sections (frontend/sections/*).
 *
 * Pure functions that return HTML strings (plus a couple of small DOM
 * helpers), so every section uses the same KPI strips, pills, findings,
 * trend lines and plan drawer instead of hand-styling each screen.
 * Loaded after app.js (uses its esc/fmtMoney/postJSON globals).
 */

const UI_API = "/api";

async function uiGet(path) {
  const res = await fetch(`${UI_API}${path}`, window.gesAuthToken ? { headers: { Authorization: `Bearer ${window.gesAuthToken}` } } : undefined);
  return gesReadJSON(res);
}

/** Money as the player reads it: short by default ($4.62M); `full: true` keeps every digit (rare). */
function uiMoney(v, { compact = true, signed = false, full = false } = {}) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  const a = Math.abs(v);
  const sign = v < 0 && Math.round(a) > 0 ? "−" : (signed && v > 0 ? "+" : "");
  if (!full && compact !== null) {
    if (a >= 1e12) return `${sign}$${(a / 1e12).toFixed(2)}T`;
    if (a >= 1e9) return `${sign}$${(a / 1e9).toFixed(2)}B`;
    if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(2)}M`;
    if (a >= 1e4) return `${sign}$${(a / 1e3).toFixed(0)}K`;
  }
  return `${sign}$${Math.round(a).toLocaleString("en-US")}`;
}

/** One line on what a cost over $100K pays for (2.7): the backend sends it as `pays`. */
function uiPays(o) {
  return o && o.pays ? `<small class="ui-pays">💼 ${esc(o.pays)}</small>` : "";
}
window.uiPays = uiPays;

function uiPct(v, digits = 1, signed = false) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return `${signed && v > 0 ? "+" : ""}${Number(v).toFixed(digits)}%`;
}

function uiTone(v, goodWhenHigh = true) {
  if (v === null || v === undefined || v === 0) return "";
  return (v > 0) === goodWhenHigh ? "pos" : "neg";
}

/** Section header: title, one-line purpose, optional right-side actions. */
function uiHeader(title, subtitle, actionsHtml = "") {
  return `
    <header class="sx-header">
      <div>
        <h2 class="sx-title">${esc(title)}</h2>
        ${subtitle ? `<p class="sx-sub">${esc(subtitle)}</p>` : ""}
      </div>
      ${actionsHtml ? `<div class="sx-header-actions">${actionsHtml}</div>` : ""}
    </header>`;
}

/** KPI strip: [{label, value, sub, tone, hint}] */
function uiKpis(items) {
  return `<div class="sx-kpis">${items.map((k) => `
    <div class="sx-kpi" ${k.hint ? `title="${esc(k.hint)}"` : ""}>
      <div class="sx-kpi-label">${esc(k.label)}</div>
      <div class="sx-kpi-value ${k.tone || ""}">${k.value}</div>
      ${k.sub ? `<div class="sx-kpi-sub ${k.subTone || ""}">${k.sub}</div>` : ""}
    </div>`).join("")}</div>`;
}

function uiPill(text, tone = "") {
  return `<span class="sx-pill ${tone}">${esc(text)}</span>`;
}

/** Circular 0-100 gauge. */
function uiRing(value, label = "", size = 64) {
  const v = Math.max(0, Math.min(100, value || 0));
  const r = size / 2 - 5, c = 2 * Math.PI * r;
  const tone = v >= 70 ? "var(--sage)" : v >= 40 ? "var(--neon-gold)" : "var(--oxblood)";
  return `
    <div class="sx-ring" style="width:${size}px;height:${size}px" role="img" aria-label="${esc(label)} ${Math.round(v)} of 100">
      <svg viewBox="0 0 ${size} ${size}" width="${size}" height="${size}">
        <circle cx="${size / 2}" cy="${size / 2}" r="${r}" fill="none" stroke="rgba(255,255,255,0.08)" stroke-width="5"/>
        <circle cx="${size / 2}" cy="${size / 2}" r="${r}" fill="none" stroke="${tone}" stroke-width="5" stroke-linecap="round"
                stroke-dasharray="${c}" stroke-dashoffset="${c * (1 - v / 100)}" transform="rotate(-90 ${size / 2} ${size / 2})"/>
      </svg>
      <div class="sx-ring-value">${Math.round(v)}</div>
    </div>`;
}

/** Small inline trend line. */
function uiSpark(values, { width = 120, height = 34, tone = "var(--brass)" } = {}) {
  const vals = (values || []).filter((v) => typeof v === "number");
  if (vals.length < 2) return `<div class="sx-spark-empty">Not enough history yet</div>`;
  const min = Math.min(...vals), max = Math.max(...vals), span = max - min || 1;
  const pts = vals.map((v, i) => `${(i / (vals.length - 1)) * width},${height - 3 - ((v - min) / span) * (height - 6)}`);
  return `<svg class="sx-spark" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" aria-hidden="true">
      <polyline points="${pts.join(" ")}" fill="none" stroke="${tone}" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>
    </svg>`;
}

/** Two-series line chart (e.g. baseline vs. with-decision). */
function uiCompareChart(a, b, { labelA = "Without", labelB = "With", width = 520, height = 150 } = {}) {
  const all = [...(a || []), ...(b || [])].filter((v) => typeof v === "number");
  if (all.length < 2) return "";
  const n = Math.max(a.length, b.length);
  // zoom to the data (with 10% headroom) so the gap between the lines shows
  const lo = Math.min(...all), hi = Math.max(...all);
  const margin = (hi - lo) * 0.1 || Math.abs(hi) * 0.05 || 1;
  const min = lo - margin, max = hi + margin, span = max - min || 1;
  const pad = 6;
  const line = (vals) => vals.map((v, i) => `${pad + (i / Math.max(1, n - 1)) * (width - 2 * pad)},${height - pad - ((v - min) / span) * (height - 2 * pad)}`).join(" ");
  return `
    <div class="sx-compare">
      <svg viewBox="0 0 ${width} ${height}" preserveAspectRatio="none" class="sx-compare-svg" aria-hidden="true">
        <polyline points="${line(a)}" fill="none" stroke="var(--text-low)" stroke-width="2" stroke-dasharray="5 4"/>
        <polyline points="${line(b)}" fill="none" stroke="var(--brass)" stroke-width="2.5"/>
      </svg>
      <div class="sx-compare-legend"><span class="sx-legend-a">${esc(labelA)}</span><span class="sx-legend-b">${esc(labelB)}</span></div>
    </div>`;
}

/** Horizontal share bar: [{label, value, tone}] */
function uiShareBar(parts) {
  const total = parts.reduce((s, p) => s + Math.max(0, p.value || 0), 0) || 1;
  return `<div class="sx-share">${parts.map((p) => `<div class="sx-share-seg ${p.tone || ""}" style="width:${(Math.max(0, p.value) / total) * 100}%" title="${esc(p.label)}: ${Math.round((p.value / total) * 100)}%"></div>`).join("")}</div>
    <div class="sx-share-legend">${parts.map((p) => `<span><i class="${p.tone || ""}"></i>${esc(p.label)} ${Math.round((Math.max(0, p.value) / total) * 100)}%</span>`).join("")}</div>`;
}

/** Diagnosis / finding row. severity: 3 critical, 2 warning, 1 note, 0 good */
function uiFinding(f) {
  const tone = f.severity >= 3 ? "crit" : f.severity === 2 ? "warn" : f.severity === 1 ? "note" : "good";
  return `<div class="sx-finding ${tone}"><div class="sx-finding-title">${esc(f.title)}</div><div class="sx-finding-detail">${esc(f.detail)}</div></div>`;
}

/** Stage stepper: stages [labels], current index, optional failed flag */
function uiStepper(stages, current, { failed = false } = {}) {
  return `<ol class="sx-stepper">${stages.map((s, i) => `
    <li class="${i < current ? "done" : i === current ? (failed ? "failed" : "current") : ""}"><span class="sx-step-dot"></span><span class="sx-step-label">${esc(s)}</span></li>`).join("")}</ol>`;
}

function uiEmpty(title, body, actionHtml = "") {
  return `<div class="sx-empty"><div class="sx-empty-title">${esc(title)}</div><p>${esc(body)}</p>${actionHtml}</div>`;
}

function uiCard(title, bodyHtml, { sub = "", actions = "", cls = "" } = {}) {
  return `<section class="sx-card ${cls}">
      ${title ? `<div class="sx-card-head"><div><h3 class="sx-card-title">${esc(title)}</h3>${sub ? `<div class="sx-card-sub">${esc(sub)}</div>` : ""}</div>${actions}</div>` : ""}
      ${bodyHtml}
    </section>`;
}

/** Tabs row; returns html; clicks call onSelect(key). */
function uiTabs(tabs, active, name) {
  return `<div class="sx-tabs" role="tablist" data-tabs="${esc(name)}">${tabs.map(([key, label]) =>
    `<button type="button" role="tab" class="sx-tab${key === active ? " active" : ""}" data-tab-key="${esc(key)}" aria-selected="${key === active}">${esc(label)}</button>`).join("")}</div>`;
}

function uiWireTabs(root, name, onSelect) {
  root.querySelectorAll(`[data-tabs="${name}"] .sx-tab`).forEach((b) => b.addEventListener("click", () => onSelect(b.dataset.tabKey)));
}

/**
 * Plan drawer: a modal for one decision — description, input, cost, a live
 * forecast (from the backend) and a Commit button.
 *   opts = { title, description, effect, risk, input, costFor(value) -> number|null,
 *            forecast(value) -> Promise<result>, renderForecast(result) -> html,
 *            commit(value) -> Promise<message>,
 *            ghost(value) -> action|null: also show the company-wide Ghost Future }
 */
/** How a slider's value reads: money, a percentage or months. */
function uiSliderText(v, kind) {
  const n = +v;
  if (kind === "pct") return `${n}%`;
  if (kind === "months") return `${n} month${n === 1 ? "" : "s"}`;
  return uiMoney(n);
}

/** A forecast lane id (backend/ghost.claim_lane): a newer forecast in the lane cancels the older one. */
function uiLane() {
  return `l${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

/** True for a forecast that was cancelled on purpose (aborted here, or replaced on the server). */
function uiCancelled(e) {
  return !!e && (e.name === "AbortError" || e.message === "superseded");
}

function uiPlanDrawer(opts) {
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  const inp = opts.input;
  let control = "";
  if (inp && (inp.kind === "amount" || inp.kind === "pct" || inp.kind === "months")) {
    control = `
      <label class="sx-field-label">${esc(inp.label)}</label>
      <div class="sx-slider-row">
        <input type="range" id="plan-range" min="${inp.min}" max="${inp.max}" step="${inp.step || 1}" value="${inp.default}" aria-label="${esc(inp.label)}">
        <input type="hidden" id="plan-value" value="${inp.default}">
        <b class="sx-slider-val" id="plan-readout">${esc(uiSliderText(inp.default, inp.kind))}</b>
      </div>`;
  } else if (inp && (inp.kind === "country" || inp.kind === "choice")) {
    const options = (inp.options || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));
    control = `
      <label class="sx-field-label">${esc(inp.label)}</label>
      <select id="plan-value">${options.map((o) => `<option value="${esc(o.value)}" ${o.value === inp.default ? "selected" : ""}>${esc(o.label)}</option>`).join("")}</select>`;
  }
  backdrop.innerHTML = `
    <div class="modal sx-drawer" role="dialog" aria-label="${esc(opts.title)}">
      <div class="sx-drawer-head">
        <div class="sx-eyebrow">${esc(opts.eyebrow || "Plan a move")}</div>
        <h3>${esc(opts.title)}</h3>
        <p class="sx-drawer-desc">${esc(opts.description || "")}</p>
        <div class="sx-drawer-meta">
          ${opts.effect ? `<div><span>Effect</span>${esc(opts.effect)}</div>` : ""}
          ${opts.risk ? `<div><span>Risk</span>${esc(opts.risk)}</div>` : ""}
        </div>
      </div>
      <div class="sx-drawer-body">
        ${control}
        <div class="sx-cost" id="plan-cost"></div>
        <div class="sx-forecast" id="plan-forecast"></div>
        ${opts.ghost ? `<div class="sx-forecast" id="plan-ghost"></div>` : ""}
      </div>
      <div class="modal-actions">
        <button type="button" id="plan-cancel">Cancel</button>
        <button type="button" class="primary" id="plan-commit">${esc(opts.commitLabel || "Commit")}</button>
      </div>
    </div>`;
  document.body.appendChild(backdrop);
  const range = backdrop.querySelector("#plan-range");
  const valueEl = backdrop.querySelector("#plan-value");
  const current = () => {
    if (!valueEl) return null;
    if (inp.kind === "country" || inp.kind === "choice") return valueEl.value;
    return parseFloat(valueEl.value);
  };
  // One forecast at a time: a new slider value cancels the one in flight (the
  // browser request and, through the lane, the server's simulation), so a
  // Commit never waits behind forecasts nobody will read.
  const lane = uiLane();
  let seq = 0, timer = null, ctrl = null;
  const stopForecast = () => { clearTimeout(timer); seq++; if (ctrl) ctrl.abort(); ctrl = null; };
  const ghostPanel = opts.ghost ? uiGhostPanel(backdrop.querySelector("#plan-ghost")) : null;
  const refresh = () => {
    const v = current();
    if (ghostPanel) ghostPanel.run(opts.ghost(v));
    const cost = opts.costFor ? opts.costFor(v) : null;
    backdrop.querySelector("#plan-cost").innerHTML = cost === null || cost === undefined ? ""
      : `<span>Cost now</span><strong>${cost > 0 ? uiMoney(cost) : "Free"}</strong>`;
    if (!opts.forecast) return;
    stopForecast();
    const fc = backdrop.querySelector("#plan-forecast");
    fc.innerHTML = `<div class="sx-forecast-loading">Running the next 12 months on a copy of your company…</div>`;
    timer = setTimeout(async () => {
      const mine = ++seq;
      ctrl = typeof AbortController === "function" ? new AbortController() : null;
      try {
        const result = await opts.forecast(v, { lane, signal: ctrl ? ctrl.signal : undefined });
        if (mine === seq) fc.innerHTML = opts.renderForecast(result);
      } catch (e) {
        if (mine === seq && !uiCancelled(e)) fc.innerHTML = `<div class="sx-forecast-error">${esc(e.message || "No forecast available.")}</div>`;
      }
    }, 600);
  };
  if (range) {
    range.addEventListener("input", () => {
      valueEl.value = range.value;
      backdrop.querySelector("#plan-readout").textContent = uiSliderText(range.value, inp.kind);
      refresh();
    });
  } else if (valueEl) {
    valueEl.addEventListener("change", refresh);
  }
  const close = () => { stopForecast(); if (ghostPanel) ghostPanel.stop(); backdrop.remove(); };
  backdrop.querySelector("#plan-cancel").addEventListener("click", close);
  backdrop.addEventListener("click", (e) => { if (e.target === backdrop) close(); });
  backdrop.querySelector("#plan-commit").addEventListener("click", async () => {
    const btn = backdrop.querySelector("#plan-commit");
    const label = btn.textContent;
    btn.disabled = true;
    btn.textContent = "Committing…";
    stopForecast();
    if (ghostPanel) ghostPanel.stop();
    try {
      const msg = await opts.commit(current());
      close();
      if (msg) toast(msg);
      await window.refreshAll();
    } catch (e) {
      btn.disabled = false;
      btn.textContent = label;
      toast(e.message || "That move couldn't be made.", true);
    }
  });
  refresh();
  return backdrop;
}

/** Standard 12-month forecast readout for a Simulation Lab result. */
function uiForecastReadout(r) {
  const d = r.deltas || {};
  const s = r.series || {};
  const row = (label, v, money = true, goodHigh = true) =>
    `<div class="sx-fc-stat"><span>${esc(label)}</span><strong class="${uiTone(v, goodHigh)}">${money ? uiMoney(v, { signed: true, compact: true }) : (v > 0 ? "+" : "") + (v ?? 0).toFixed(1)}</strong></div>`;
  return `
    <div class="sx-fc-title">Forecast — next ${r.horizon_months || 12} months vs. doing nothing</div>
    <div class="sx-fc-grid">
      ${row("Company profit", d.company_net_income)}
      ${row("Cash at the end", d.end_cash)}
      ${row("Product revenue", d.cumulative_revenue)}
      ${d.final_satisfaction ? row("Satisfaction", d.final_satisfaction, false) : ""}
    </div>
    ${uiCompareChart(s.baseline_revenue || [], s.scenario_revenue || [], { labelA: "Do nothing", labelB: "With this move" })}
    <div class="sx-fc-note">${esc(r.note || "")}</div>`;
}


/** Trend chart with a zero baseline, value labels and month markers. */
function uiTrendChart(values, { labels = [], width = 640, height = 150, money = true } = {}) {
  const vals = (values || []).map((v) => (typeof v === "number" ? v : 0));
  if (vals.length < 2) return "";
  const max = Math.max(...vals, 0) * 1.1 || 1, min = Math.min(0, ...vals);
  const padL = 56, padR = 8, padT = 10, padB = 20, w = width - padL - padR, h = height - padT - padB;
  const x = (i) => padL + (i / (vals.length - 1)) * w;
  const y = (v) => padT + h - ((v - min) / (max - min || 1)) * h;
  const fmt = (v) => (money ? uiMoney(v, { compact: true }) : Math.round(v).toLocaleString("en-US"));
  const pts = vals.map((v, i) => `${x(i)},${y(v)}`).join(" ");
  const area = `${x(0)},${y(Math.max(min, 0))} ${pts} ${x(vals.length - 1)},${y(Math.max(min, 0))}`;
  const ticks = [max, max / 2, Math.max(min, 0)];
  const step = Math.max(1, Math.ceil(vals.length / 6));
  return `<svg class="sx-trend" viewBox="0 0 ${width} ${height}" width="100%" role="img" aria-label="Trend">
      ${ticks.map((t) => `<line x1="${padL}" x2="${width - padR}" y1="${y(t)}" y2="${y(t)}" stroke="rgba(255,255,255,0.06)"/>
        <text x="${padL - 6}" y="${y(t) + 4}" text-anchor="end" class="sx-axis">${fmt(t)}</text>`).join("")}
      <polygon points="${area}" fill="rgba(126,232,250,0.08)"/>
      <polyline points="${pts}" fill="none" stroke="var(--brass)" stroke-width="2.2" stroke-linejoin="round"/>
      ${vals.map((v, i) => (i % step === 0 || i === vals.length - 1) && labels[i] !== undefined
        ? `<text x="${x(i)}" y="${height - 4}" text-anchor="middle" class="sx-axis">${esc(String(labels[i]))}</text>` : "").join("")}
    </svg>`;
}

/** Fill "{value}" / "{value_fraction}" / "{value_int}" placeholders in an action template. */
function uiFillAction(template, value) {
  const fill = (v) => {
    if (v === "{value}") return value;
    if (v === "{value_fraction}") return value / 100;
    if (v === "{value_int}") return Math.round(value);
    if (v && typeof v === "object" && !Array.isArray(v)) return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, fill(x)]));
    return v;
  };
  return fill(JSON.parse(JSON.stringify(template)));
}

/**
 * Ghost Futures chart (Phase 7): "do nothing" as a solid line, the decision
 * as a ghost: a dashed most-likely line inside a band that spans the p10..p90
 * of the futures. Zero is marked when cash can go negative.
 */
function uiGhostChart(r, key, { title, height = 150 } = {}) {
  const band = (r.band || {})[`scenario_${key}`], base = (r.band || {})[`baseline_${key}`];
  const ghost = ((r.series || {})[`scenario_${key}`]) || [];
  const plain = ((r.series || {})[`baseline_${key}`]) || [];
  const n = Math.max(ghost.length, plain.length, band ? band.p50.length : 0);
  if (n < 2) return "";
  const W = 320, H = height, pl = 6, pr = 50, pt = 18, pb = 18;
  const all = [...ghost, ...plain, ...(band ? [...band.p10, ...band.p90] : []), ...(key === "cash" ? [0] : [])];
  let lo = Math.min(...all), hi = Math.max(...all);
  if (hi - lo < 1) { hi += 1; lo -= 1; }
  const pad = (hi - lo) * 0.08; lo -= pad; hi += pad;
  const X = (i) => pl + ((W - pl - pr) * i) / (n - 1), Y = (v) => pt + (H - pt - pb) * (1 - (v - lo) / (hi - lo));
  const path = (vals) => vals.map((v, i) => `${i ? "L" : "M"}${X(i).toFixed(1)},${Y(v).toFixed(1)}`).join(" ");
  const area = band ? `${path(band.p90)} ${band.p10.map((v, i) => `L${X(band.p10.length - 1 - i).toFixed(1)},${Y(band.p10[band.p10.length - 1 - i]).toFixed(1)}`).join(" ")} Z` : "";
  const zero = lo < 0 && hi > 0 ? `<line x1="${pl}" x2="${W - pr}" y1="${Y(0).toFixed(1)}" y2="${Y(0).toFixed(1)}" class="gh-zero"/><text x="${W - pr + 4}" y="${(Y(0) + 3).toFixed(1)}" class="gh-axis gh-neg">$0</text>` : "";
  const ticks = [hi - pad, (hi + lo) / 2].map((v) => `<line x1="${pl}" x2="${W - pr}" y1="${Y(v).toFixed(1)}" y2="${Y(v).toFixed(1)}" class="gh-grid"/><text x="${W - pr + 4}" y="${(Y(v) + 3).toFixed(1)}" class="gh-axis">${uiMoney(v, { compact: true })}</text>`).join("");
  const sm = r.start_month || 0;
  const months = [0, n - 1].map((i) => `<text x="${X(i).toFixed(1)}" y="${H - 4}" text-anchor="${i ? "end" : "start"}" class="gh-axis">M${sm + i + 1}</text>`).join("");
  const last = ghost.length ? ghost[ghost.length - 1] : 0;
  return `<figure class="gh-chart"><figcaption>${esc(title)}</figcaption>
    <svg viewBox="0 0 ${W} ${H}" role="img" aria-label="${esc(title)}: with this move ${uiMoney(last, { compact: true })} after ${n} months">
      ${ticks}${zero}
      ${base ? `<path d="${path(base.p50)}" class="gh-base"/>` : plain.length ? `<path d="${path(plain)}" class="gh-base"/>` : ""}
      ${area ? `<path d="${area}" class="gh-band"/>` : ""}
      <path d="${path(ghost)}" class="gh-line"/>
      <circle cx="${X(ghost.length - 1).toFixed(1)}" cy="${Y(last).toFixed(1)}" r="3.5" class="gh-dot"/>
      ${months}
      <text x="${pl + 2}" y="11" class="gh-mark">FORECAST</text>
    </svg></figure>`;
}

/** "+$1.2M" with its p10..p90 range from the futures. */
function uiGhostRange(v, range) {
  const main = uiMoney(v, { signed: true, compact: true });
  if (!range || range[0] === range[1]) return main;
  return `${main}<small>${uiMoney(range[0], { signed: true, compact: true })} to ${uiMoney(range[1], { signed: true, compact: true })}</small>`;
}

/** Ghost runway: when cash runs out with the move, in how many futures. */
function uiGhostRunway(r) {
  const run = r.runway;
  if (!run) return "";
  const w = run.with, wo = run.without, n = r.seeds || 1, sm = r.start_month || 0;
  if (!w.futures_out) {
    return `<div class="gh-runway gh-ok"><b>Ghost runway</b> Cash stays above zero in all ${n} futures for ${r.horizon_months} months${wo.futures_out ? ` (without the move it runs out in ${wo.futures_out} of ${n})` : ""}.</div>`;
  }
  return `<div class="gh-runway gh-bad"><b>Ghost runway</b> Cash runs out in <b>${w.futures_out} of ${n}</b> futures, first in month ${sm + w.first_month}${wo.futures_out ? `; without the move, ${wo.futures_out} of ${n}` : "; without the move it doesn't"}.</div>`;
}

/** Company-wide forecast readout for a Ghost Futures result (/api/forecast). */
function uiGhostReadout(r) {
  const d = r.deltas || {}, g = r.ranges || {};
  const stat = (label, k) => `<div class="sx-fc-stat"><span>${esc(label)}</span><strong class="${uiTone(d[k])}">${uiGhostRange(d[k], g[k])}</strong></div>`;
  return `
    <div class="sx-fc-title gh-title"><span class="gh-badge">👻 Ghost future</span> Next ${r.horizon_months} months vs. doing nothing${r.seeds > 1 ? ` · ${r.seeds} possible futures` : ""}</div>
    <div class="sx-fc-grid">${stat("Company profit", "net_income")}${stat("Cash at the end", "end_cash")}${stat("Revenue", "revenue")}</div>
    ${uiGhostRunway(r)}
    <div class="gh-charts">${uiGhostChart(r, "cash", { title: "Cash" })}${uiGhostChart(r, "revenue", { title: "Monthly revenue" })}</div>
    <div class="gh-legend"><span><i class="gh-k-base"></i>Do nothing</span><span><i class="gh-k-line"></i>With this move (most likely)</span>${r.band ? `<span><i class="gh-k-band"></i>Range of futures</span>` : ""}</div>
    <div class="sx-fc-note">${esc(r.note || "")}</div>`;
}

/**
 * A Ghost Futures panel for any confirm step: panel.run(action) forecasts it
 * (debounced; only the latest answer is shown) and keeps it as window.gesGhost
 * (plus a "ges:ghost" event), so the World district can draw the ghost building.
 */
function uiGhostPanel(el, { months = 12, seeds = 3 } = {}) {
  if (!el) return { run() {}, stop() {} };
  el.classList.add("gh-panel");
  const lane = uiLane();
  let seq = 0, timer = null, ctrl = null;
  const stop = () => { clearTimeout(timer); seq++; if (ctrl) ctrl.abort(); ctrl = null; };
  return {
    stop,
    run(action) {
      stop();
      if (!action) { el.innerHTML = ""; return; }
      el.innerHTML = `<div class="sx-forecast-loading">👻 Running ${seeds} possible futures on copies of your company…</div>`;
      timer = setTimeout(async () => {
        const mine = ++seq;
        ctrl = typeof AbortController === "function" ? new AbortController() : null;
        try {
          const r = await postJSON("/forecast", { action, months, seeds, lane }, { signal: ctrl ? ctrl.signal : undefined });
          if (mine !== seq || !el.isConnected) return;
          el.innerHTML = uiGhostReadout(r);
          // the World district draws the building this decision would add (world/buildgen.ghostFor)
          window.gesGhost = { action, result: r };
          window.dispatchEvent(new CustomEvent("ges:ghost", { detail: window.gesGhost }));
        } catch (e) {
          if (mine === seq && !uiCancelled(e)) el.innerHTML = `<div class="sx-forecast-error">${esc(e.message || "No forecast for this move.")}</div>`;
        }
      }, 650);
    },
  };
}

/** Open a plan drawer for a company-wide action template (forecast + commit). */
function uiOpenAction({ eyebrow, title, description, effect, risk, input, action, costFor, commitLabel }) {
  return uiPlanDrawer({
    eyebrow, title, description, effect, risk, input, commitLabel,
    costFor: costFor || ((v) => (input && input.kind === "amount" ? v : 0)),
    forecast: (v, { lane, signal } = {}) => postJSON("/forecast", { action: uiFillAction(action, v), months: 12, seeds: 3, lane }, { signal }),
    renderForecast: uiGhostReadout,
    commit: async (v) => (await postJSON("/actions/execute", { action: uiFillAction(action, v) })).message,
  });
}

/**
 * Isometric 3D bar chart (inline SVG, no library): each bar is a prism with
 * a lit top, a front face and a shaded side, so comparisons read like a
 * physical model. items: [{label, value, sub?, highlight?, tone?}].
 * Values are drawn as given; the caller formats them via `format`.
 */
function ui3DBars(items, { height = 210, format = (v) => uiMoney(v, { compact: true }), caption = "", log = false } = {}) {
  if (!items || !items.length) return "";
  const n = items.length;
  const slot = 112, barW = 54, depth = 18, base = height - 46, top = 34;
  const width = n * slot + depth + 16;
  // Log scale for ranges that span orders of magnitude (a startup next to a
  // $600B giant); heights then compare digits, and labels keep real values.
  const sv = (v) => (log ? Math.log10(Math.max(1, v || 0)) : Math.max(0, v || 0));
  const floorV = log ? Math.min(...items.map((i) => sv(i.value))) * 0.85 : 0;
  const max = Math.max(...items.map((i) => sv(i.value) - floorV), 1e-9);
  const faces = { normal: ["#2b6f86", "#3f97b3", "#1d4c5c"], highlight: ["#b8862b", "#e5b44f", "#7f5c1c"],
                  muted: ["#3a4150", "#4c5568", "#2a303b"] };
  const bars = items.map((it, i) => {
    const h = Math.max(3, ((sv(it.value) - floorV) / max) * (base - top));
    const x = 12 + i * slot + (slot - barW - depth) / 2, y = base - h;
    const [front, lit, side] = faces[it.highlight ? "highlight" : (it.tone === "muted" ? "muted" : "normal")];
    const d = depth, dy = depth * 0.55;
    return `<g class="sx-3d-bar${it.highlight ? " hi" : ""}">
      <polygon points="${x + barW},${y} ${x + barW + d},${y - dy} ${x + barW + d},${base - dy} ${x + barW},${base}" fill="${side}"/>
      <rect x="${x}" y="${y}" width="${barW}" height="${h}" fill="${front}"/>
      <polygon points="${x},${y} ${x + d},${y - dy} ${x + barW + d},${y - dy} ${x + barW},${y}" fill="${lit}"/>
      <text x="${x + barW / 2 + d / 2}" y="${y - dy - 6}" text-anchor="middle" class="sx-3d-val">${esc(format(it.value))}</text>
      <text x="${x + barW / 2}" y="${base + 16}" text-anchor="middle" class="sx-3d-label">${esc(String(it.label).slice(0, 18))}</text>
      ${it.sub ? `<text x="${x + barW / 2}" y="${base + 30}" text-anchor="middle" class="sx-3d-sub">${esc(it.sub)}</text>` : ""}
      <title>${esc(it.label)}: ${esc(format(it.value))}${it.sub ? ` · ${esc(it.sub)}` : ""}</title>
    </g>`;
  }).join("");
  const floor = `<polygon points="6,${base} ${6 + 18},${base - 10} ${width - 2},${base - 10} ${width - 20},${base}" class="sx-3d-floor"/>`;
  return `<figure class="sx-3d">${caption ? `<figcaption>${esc(caption)}</figcaption>` : ""}
    <div class="sx-3d-scroll"><svg viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" role="img" aria-label="${esc(caption || "3D bar chart")}">${floor}${bars}</svg></div></figure>`;
}

/** Horizontal meter: value against a target, e.g. budget vs required. */
function uiMeter(value, target, { label = "", format = (v) => uiMoney(v, { compact: true }) } = {}) {
  const pct = target > 0 ? Math.min(100, (value / target) * 100) : 100;
  const tone = pct >= 99.5 ? "good" : pct >= 60 ? "warn" : "bad";
  return `<div class="sx-meter ${tone}">
    ${label ? `<div class="sx-meter-head"><span>${esc(label)}</span><span class="mono">${esc(format(value))} / ${esc(format(target))}</span></div>` : ""}
    <div class="sx-meter-track"><div class="sx-meter-fill" style="width:${pct.toFixed(1)}%"></div></div></div>`;
}

/** Radar chart (inline SVG): axes [{axis, weight 0-100}]. Lives in the chart kit (charts/radar.js). */
const uiRadar = svgRadar;

/** Odds build-up: a base range plus signed nudges, ending at the final range. */
function uiOddsBuild(o) {
  const pct = (v) => `${Math.round(v * 100)}%`;
  const max = 0.06;
  const rows = o.nudges.map((nd) => {
    const w = Math.min(50, (Math.abs(nd.value) / max) * 50);
    const tone = nd.value > 0 ? "pos" : nd.value < 0 ? "neg" : "";
    return `<div class="sx-odds-row"><span>${esc(nd.label)}</span>
      <div class="sx-odds-track"><div class="sx-odds-bar ${tone}" style="${nd.value >= 0 ? "left:50%" : `left:${50 - w}%`};width:${w}%"></div></div>
      <b class="${tone}">${nd.value > 0 ? "+" : ""}${(nd.value * 100).toFixed(1)}pt</b></div>`;
  }).join("");
  return `<div class="sx-odds">
    <div class="sx-odds-row head"><span>Base chance (${esc(o.deal_type)})</span><div></div><b>${pct(o.base_low)}–${pct(o.base_high)}</b></div>
    ${rows}
    <div class="sx-odds-row total"><span>Chance it closes</span>
      <div class="sx-odds-range"><div style="left:${o.low * 100}%;width:${Math.max(1, (o.high - o.low) * 100)}%"></div></div>
      <b>${pct(o.low)}–${pct(o.high)}</b></div></div>`;
}

/**
 * A document on the CEO's desk (backend/correspondence.py shape). With decisions it is
 * decision-first: subject, verdict, key figures, the recommendation and centred buttons,
 * with the full letter folded underneath; without decisions it reads as a letter.
 * Decisions carry data-doc-action (JSON engine action) or data-doc-client.
 */
function uiDocument(doc) {
  const fig = (doc.figures || []).map((f) => `<div><span>${esc(f.label)}</span><b class="${f.tone || ""}">${esc(f.value)}</b></div>`).join("");
  // Decisions: centred, real buttons. The recommended choice first and filled; others outlined;
  // "pass"-style choices last as a quiet text button. Each says what it does underneath.
  const order = (d) => (d.best ? 0 : d.danger ? 2 : 1);
  const dec = (doc.decisions || []).map((d, i) => ({ d, i })).sort((x, y) => order(x.d) - order(y.d) || x.i - y.i).map(({ d, i }) => `
    <div class="doc-choice ${d.best ? "best" : ""} ${d.danger ? "pass" : ""}">
      ${d.best ? `<span class="doc-best">★ Recommended</span>` : ""}
      <button type="button" class="doc-decision ${d.best ? "best" : ""} ${d.danger ? "danger" : ""}" ${d.disabled ? "disabled" : ""}
        data-doc-i="${i}" ${d.action ? `data-doc-action='${esc(JSON.stringify(d.action))}'` : ""} ${d.client ? `data-doc-client="${esc(d.client)}"` : ""}
        ${d.confirm ? `data-confirm="${esc(d.confirm)}"` : ""}><b>${esc(d.label)}</b>${d.danger ? "" : `<i aria-hidden="true">→</i>`}</button>
      <p class="doc-why">${esc(d.disabled && d.reason ? d.reason : d.detail)}</p>
    </div>`).join("");
  const paras = doc.paragraphs || [];
  if (dec) {
    // Decision-first: what it is, the numbers, the recommendation and the buttons up top;
    // the full letter folds away underneath for anyone who wants the detail.
    const recIdx = paras.findIndex((p) => /^(My recommendation|Recommendation|I recommend|My advice)/i.test(p));
    const rec = recIdx >= 0 ? paras[recIdx].replace(/^(My recommendation|Recommendation|My advice):?\s*/i, "") : "";
    const lead = paras.find((p, k) => k !== recIdx) || "";
    const tone = (doc.figures || []).find((f) => f.tone);
    return `
    <article class="doc doc-${esc(doc.kind)} doc-quick">
      <div class="dq-eyebrow">${esc(doc.letterhead)} · Month ${doc.month} · from ${esc(doc.from_name)}, ${esc(doc.from_title)}</div>
      <h3 class="doc-subject dq-title">${esc(doc.subject)}</h3>
      ${tone ? `<span class="dq-verdict ${esc(tone.tone)}">${tone.tone === "pos" ? "Worth it" : "Think twice"}: ${esc(tone.label.toLowerCase())} ${esc(tone.value)}</span>` : ""}
      ${fig ? `<div class="doc-figures dq-figures">${fig}</div>` : ""}
      <div class="dq-brief">${lead ? `<p>${esc(lead)}</p>` : ""}${rec ? `<p class="dq-rec"><b>Recommendation:</b> ${esc(rec)}</p>` : ""}</div>
      <div class="doc-reply"><div class="doc-reply-title">Your decision</div><div class="doc-decisions">${dec}</div></div>
      <div class="doc-result" data-doc-result></div>
      ${paras.length > 1 || doc.ps ? `<details class="dq-more"><summary>Read ${esc(doc.from_name.split(" ")[0])}'s full memo</summary>
        <div class="doc-body">${paras.map((p) => `<p>${esc(p)}</p>`).join("")}</div>
        ${doc.ps ? `<p class="doc-ps">P.S. ${esc(doc.ps)}</p>` : ""}
        <div class="doc-sign">${esc(doc.from_name)}<span>${esc(doc.from_title)}</span></div></details>` : ""}
    </article>`;
  }
  return `
    <article class="doc doc-${esc(doc.kind)}">
      <div class="doc-letterhead">${esc(doc.letterhead)}</div>
      <div class="doc-meta">
        <div><span>From</span><b>${esc(doc.from_name)}</b><em>${esc(doc.from_title)}</em></div>
        <div><span>To</span><b>${esc(doc.to)}</b></div>
        <div><span>Date</span><b>Month ${doc.month}</b></div>
      </div>
      <h3 class="doc-subject">${esc(doc.subject)}</h3>
      <div class="doc-body">${paras.map((p) => `<p>${esc(p)}</p>`).join("")}</div>
      ${fig ? `<div class="doc-figures">${fig}</div>` : ""}
      ${doc.ps ? `<p class="doc-ps">P.S. ${esc(doc.ps)}</p>` : ""}
      <div class="doc-sign">${esc(doc.from_name)}<span>${esc(doc.from_title)}</span></div>
      <div class="doc-result" data-doc-result></div>
    </article>`;
}

// Late-bound for core modules, which load before ui/kit.js (the Ghost Futures panel in their modals).
window.uiGhostPanel = uiGhostPanel;

export { ui3DBars, uiCard, uiDocument, uiEmpty, uiFinding, uiForecastReadout, uiGet, uiGhostChart, uiGhostPanel, uiGhostReadout, uiHeader, uiKpis, uiMeter, uiMoney, uiOddsBuild, uiPays, uiOpenAction, uiPct, uiPill, uiPlanDrawer, uiRadar, uiRing, uiShareBar, uiSpark, uiTabs, uiTone, uiTrendChart, uiWireTabs };
